import { Button } from '@components/ui/button'
import { DropCapInsetIcon, DropCapOutsetIcon } from '@components/ui/custom-icons'
import { Switch } from '@components/ui/switch'
import {
  DROP_CAP_LINES_MAX,
  DROP_CAP_LINES_MIN,
  DROP_CAP_OFFSET_MAX,
  DROP_CAP_OFFSET_MIN,
  DROP_CAP_OFFSET_PRESET_NAMES,
} from '@/lib/text/drop-cap'
import { cn } from '@/utils'
import { keepTextEditFocus } from '../handlers/keep-text-edit-focus'
import { useClearSelectionCommand } from '../hooks/use-clear-selection-command'
import type { DropCapControls } from '../hooks/use-drop-cap'
import { DropCapNumberField } from './drop-cap-number-field'
import { IconToggleButton } from './icon-toggle-button'

/**
 * Drop Cap Section — always visible when a compatible object is selected, but only
 * interactive when the selection is a genuine text frame (`dropCapControlsDisabled`).
 * Sub-controls (icons, width chips, Lines, Offset) are further disabled only when NO
 * paragraph in the selection has a drop cap (`dropCapSubControlsDisabled`, gated on
 * `dropCap`) — a MIXED selection (some paragraphs have one, some don't) shows the
 * switch fully ON and every sub-control live, not disabled: Lines/Offset/style each
 * already render their own "Mixed" placeholder when the on paragraphs disagree,
 * so there's no separate reason to also lock the controls themselves.
 */
export function DropCapSection({ controls }: { controls: DropCapControls }) {
  const subDisabled = controls.dropCapSubControlsDisabled
  const clearSelection = useClearSelectionCommand()

  return (
    <div
      className={cn('space-y-3', controls.dropCapControlsDisabled && 'opacity-60')}
      onPointerDown={keepTextEditFocus}
    >
      <div className="flex items-center gap-2">
        <Switch
          id="drop-cap"
          checked={controls.dropCap}
          disabled={controls.dropCapControlsDisabled}
          onCheckedChange={controls.handleSwitchChange}
        />
        <label
          htmlFor="drop-cap"
          className="text-sm font-medium text-base-foreground leading-normal"
        >
          Drop Cap
        </label>
      </div>
      <div className="flex gap-1 mt-2">
        {/* Longer delay than the panel-wide default (`TooltipProvider delayDuration={0}`,
         *  instant elsewhere): these two sit directly over the Inside/Outside
         *  toggle a user is switching between quickly, and an instant tooltip popping up
         *  mid-click was reported to interfere with hitting the second button. */}
        <IconToggleButton
          icon={DropCapInsetIcon}
          label="Inside (Drop Cap)"
          pressed={controls.dropCapStyle === 'INS'}
          disabled={subDisabled}
          onClick={() => controls.handleIconClick('INS')}
          tooltipDelay={400}
          onEscape={clearSelection}
        />
        <IconToggleButton
          icon={DropCapOutsetIcon}
          label="Outside (Raised Cap)"
          pressed={controls.dropCapStyle === 'OUT'}
          disabled={subDisabled}
          onClick={() => controls.handleIconClick('OUT')}
          tooltipDelay={400}
          onEscape={clearSelection}
        />
      </div>
      <div className="flex gap-2">
        {DROP_CAP_OFFSET_PRESET_NAMES.map((name) => (
          <Button
            key={name}
            variant="ghost"
            size="xs"
            disabled={subDisabled}
            onClick={() => controls.handleOffsetPresetClick(name)}
            className={cn(
              'font-medium text-base-foreground',
              controls.activeDropCapOffsetPreset === name && 'bg-accent text-accent-foreground'
            )}
          >
            {name}
          </Button>
        ))}
      </div>

      <DropCapNumberField
        label="Lines"
        field={controls.lines}
        min={DROP_CAP_LINES_MIN}
        max={DROP_CAP_LINES_MAX}
        suffix="num"
        errorId="drop-cap-lines-error"
        disabled={subDisabled}
      />

      <DropCapNumberField
        label="Offset"
        field={controls.offset}
        min={DROP_CAP_OFFSET_MIN}
        max={DROP_CAP_OFFSET_MAX}
        suffix="pt"
        errorId="drop-cap-offset-error"
        disabled={subDisabled}
      />
    </div>
  )
}
